// v52 — the Tier Navigator. Renamed from SpineModal.
//
// "The spine" was our internal word for the five-tier supply chain and it read
// as jargon on screen, so the surface now says what it does: these are the
// PLATFORM TIERS, and this is how you move between the ones you hold.
//
// ── Why this cannot offer something the server would refuse ────────────────
//
// Rows are drawn from `catalogue` (every active tier) and their state comes
// from `actable` (this caller's grants). Both arrive from /api/session/context,
// and `actable` is the output of resolve_actable_tiers() — the SAME function
// the request guard reads. One definition, so the navigator and the guard
// cannot disagree.
//
// ── Three row states, and the badge colour carries them ───────────────────
//
//   HERE      blue badge      the tier being acted as right now
//   ENTER     navy badge      held, one click away
//   LOCKED    grey badge      not in the plan — a price, not an error
//
// Colour is never the only signal: each state also carries its own words, so
// the row still reads correctly in monochrome or to a colour-blind user.
//
// ── The admin exception ───────────────────────────────────────────────────
//
// A platform admin can PREVIEW every tier, because demonstrating the product
// to a prospect means showing tiers nobody in the room has bought. That is a
// presentation affordance and nothing more — previewing does not switch the
// acting context, because the server would refuse the next request and the
// screen would break in front of the prospect. The badge says ADMIN VIEW so
// nobody mistakes what they are looking at for a live tenant's account.

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useActingContext, useSetActingTier } from "@/lib/context/useActingContext";
import { useShellUser } from "@/features/auth/AuthContext";
import type { TierCode, TierRow, ActableTier } from "@/lib/context/actingSlice";

interface TierNavigatorProps {
  open: boolean;
  onClose: () => void;
}

/** The four-bar mark: three narrow, one full-width foot. */
export function SpineMark({ size = 18, accent = "var(--color-lc-blue)" }: { size?: number; accent?: string }) {
  return (
    <svg viewBox="0 0 78 100" width={size * 0.78} height={size} aria-hidden="true">
      <rect x="0" y="0"  width="32" height="19" rx="3" fill={accent} />
      <rect x="0" y="27" width="32" height="19" rx="3" fill="currentColor" />
      <rect x="0" y="54" width="32" height="19" rx="3" fill="currentColor" />
      <rect x="0" y="81" width="78" height="19" rx="3" fill="currentColor" />
    </svg>
  );
}

function Padlock() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

type RowState = "here" | "enter" | "locked";

/** Badge fill per state. Held tiers are solid; locked ones recede. */
const BADGE: Record<RowState, { bg: string; fg: string }> = {
  here:   { bg: "var(--color-lc-blue)", fg: "#ffffff" },
  enter:  { bg: "var(--color-lc-navy)", fg: "#ffffff" },
  locked: { bg: "#b8c2d0",              fg: "#ffffff" },
};

export function TierNavigator({ open, onClose }: TierNavigatorProps) {
  const { catalogue, actable, tier, companyId, memberships } = useActingContext();
  const user = useShellUser();
  const setTier = useSetActingTier();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);

  const isPlatformAdmin = !!user?.isAdmin;

  /* Esc closes, and focus moves into the panel so a keyboard user is not left
     behind on the trigger. */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    panelRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const nameFor = (id: string | null) =>
    memberships.find((m) => m.company_id === id)?.name ?? null;

  /* One row per catalogue tier. A tier held at SEVERAL companies produces
     several grants — each is its own row, because a row is a (tier, company)
     pair rather than just a tier. */
  type Row = { tier: TierRow; grant: ActableTier | null };
  const rows: Row[] = catalogue.flatMap((t): Row[] => {
    const grants = actable.filter((a) => a.tier_code === t.code);
    if (grants.length === 0) return [{ tier: t, grant: null }];
    return grants.map((g) => ({ tier: t, grant: g }));
  });

  const go = (code: TierCode, company: string | null, home: string | null) => {
    setTier(code, company);
    onClose();
    /* Land on the tier's home. Staying put can leave the user on a screen that
       does not exist for the tier they just switched to, which reads as the
       app breaking rather than as a switch. */
    navigate(home || "/today");
  };

  return (
    <div
      className="lc-v2 fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgb(10 22 40 / .55)" }}
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tier-navigator-title"
        onClick={(e) => e.stopPropagation()}
        /* THE PANEL IS THE SCROLL CONTAINER, not the overlay.
           Centring a panel taller than the viewport inside a scrolling overlay
           clips it at the TOP — the overflow goes above the scroll origin, so
           the header is unreachable however far you scroll. Capping the panel
           and letting the tier list scroll inside it keeps the header and the
           footer pinned and reachable, which matters on a laptop where five
           rows plus chrome exceeds the height. */
        className="w-full max-w-xl max-h-[calc(100vh-2rem)] flex flex-col rounded-lc-lg outline-none overflow-hidden bg-white"
        style={{ boxShadow: "0 24px 60px -12px rgb(10 22 40 / .45)" }}
      >
        {/* ── Header: the one dark band, so the tiers below read as content ── */}
        <header
          className="shrink-0 flex items-start gap-3 px-5 py-4"
          style={{ background: "var(--color-lc-navy)" }}
        >
          <span className="shrink-0 mt-0.5" style={{ color: "#ffffff" }}>
            <SpineMark size={22} />
          </span>

          <div className="min-w-0 flex-1">
            <h2 id="tier-navigator-title" className="text-[15px] font-bold text-white leading-tight">
              Platform tiers
            </h2>
            <p className="text-[11.5px] mt-0.5" style={{ color: "rgb(255 255 255 / .62)" }}>
              Navigate Labour Core by tier · owner → operative
            </p>
          </div>

          {isPlatformAdmin && (
            /* Says what you are looking at. Without it, an admin demoing four
               unbought tiers looks identical to a tenant who owns them all. */
            <span
              className="shrink-0 inline-flex items-center gap-1 text-[10px] font-extrabold tracking-wide px-2 py-1 rounded-lc-tag"
              style={{
                background: "rgb(176 117 20 / .18)",
                color: "#e8b45c",
                border: "1px solid rgb(232 180 92 / .35)",
              }}
            >
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="3" strokeLinecap="round" aria-hidden="true">
                <path d="M12 3l7 4v5c0 4.4-3 8.4-7 9-4-.6-7-4.6-7-9V7z" />
              </svg>
              ADMIN VIEW
            </span>
          )}

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 -mr-1 -mt-1 p-1.5 rounded-lc-sm transition-colors"
            style={{ color: "rgb(255 255 255 / .55)" }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        {/* ── The five tiers ─────────────────────────────────────────────── */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 flex flex-col gap-2">
          {rows.map(({ tier: t, grant }, i) => {
            const held = grant !== null;
            const here = held && t.code === tier && (grant!.company_id ?? null) === companyId;
            const state: RowState = here ? "here" : held ? "enter" : "locked";
            const company = held ? nameFor(grant!.company_id) : null;
            const badge = BADGE[state];

            return (
              <button
                key={`${t.code}-${grant?.company_id ?? "locked"}-${i}`}
                type="button"
                disabled={!held}
                aria-current={here ? "true" : undefined}
                onClick={() => held && go(t.code, grant!.company_id, t.home_route)}
                className="w-full text-left flex items-center gap-3 px-3 py-3 rounded-lc-md transition-colors"
                style={{
                  border: `1px solid ${here ? "var(--color-lc-blue-border)" : "var(--color-lc-line)"}`,
                  background: here ? "var(--color-lc-blue-light)" : "#ffffff",
                  cursor: held ? "pointer" : "default",
                }}
              >
                {/* Rank, as a number. The order IS the supply chain, so the
                    numbering carries meaning rather than decorating the row. */}
                <span
                  className="shrink-0 w-9 h-9 rounded-lc-sm inline-flex items-center justify-center text-[15px] font-extrabold"
                  style={{ background: badge.bg, color: badge.fg }}
                  aria-hidden="true"
                >
                  {t.rank}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-2 flex-wrap">
                    <span
                      className="text-[13.5px] font-bold"
                      style={{ color: state === "locked" ? "var(--color-lc-mute)" : "var(--color-lc-ink)" }}
                    >
                      {t.name}
                    </span>
                    {company && (
                      <span className="text-[11px] font-semibold" style={{ color: "var(--color-lc-blue)" }}>
                        {company}
                      </span>
                    )}
                  </span>
                  <span
                    className="block text-[11.5px] mt-0.5 leading-snug"
                    style={{ color: state === "locked" ? "var(--color-lc-faint)" : "var(--color-lc-ink-2)" }}
                  >
                    {t.strapline}
                    {t.is_free && <span style={{ color: "var(--color-lc-faint)" }}> — free</span>}
                  </span>
                </span>

                {/* ── Right column: state on top, commercials beneath ────── */}
                <span className="shrink-0 flex flex-col items-end gap-1">
                  {here && (
                    <span
                      className="text-[10px] font-extrabold tracking-wide px-2 py-1 rounded-lc-tag"
                      style={{ background: "#ffffff", color: "var(--color-lc-blue-dark)",
                               border: "1px solid var(--color-lc-blue-border)" }}
                    >
                      YOU ARE HERE
                    </span>
                  )}

                  {state === "enter" && (
                    <span className="text-[12px] font-bold inline-flex items-center gap-1"
                          style={{ color: "var(--color-lc-blue)" }}>
                      Enter
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                           strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M5 12h14M13 6l6 6-6 6" />
                      </svg>
                    </span>
                  )}

                  {state === "locked" && (
                    <span className="inline-flex items-center gap-1.5">
                      {t.package_label && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold"
                              style={{ color: "var(--color-lc-mute)" }}>
                          <Padlock />
                          {t.package_label}
                        </span>
                      )}
                      {/* PREVIEW only for an admin: for a tenant the tier is
                          simply not theirs, and dangling "preview" at someone
                          who cannot preview is worse than saying nothing. */}
                      <span
                        className="text-[9.5px] font-extrabold tracking-wide px-1.5 py-0.5 rounded-lc-tag"
                        style={{ color: "var(--color-lc-faint)", border: "1px solid var(--color-lc-line)" }}
                      >
                        {isPlatformAdmin ? "PREVIEW" : "LOCKED"}
                      </span>
                    </span>
                  )}

                  {t.fee_label && (
                    <span
                      className="lc-figure text-[11px]"
                      style={{ color: state === "locked" ? "var(--color-lc-faint)" : "var(--color-lc-mute)" }}
                    >
                      {t.fee_label}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>

        {/* ── The rule, stated once, at the bottom ───────────────────────── */}
        <footer
          className="shrink-0 mx-4 mb-4 px-4 py-3 rounded-lc-md text-[11.5px] leading-relaxed"
          style={{ background: "var(--color-lc-bg-soft)", border: "1px solid var(--color-lc-line)",
                   color: "var(--color-lc-ink-2)" }}
        >
          Switching tier is gated by <strong>subscription</strong> — a live tenant can
          only enter the tiers in their plan; the rest need an upgrade or a{" "}
          <strong>custom pack</strong> (contact support).
          {isPlatformAdmin && (
            <> <strong>This is an admin view</strong>, so it can preview every tier
            for demonstration.</>
          )}
        </footer>
      </div>
    </div>
  );
}
